import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import { useBoardMenuOptional } from '../../context/BoardMenuContext';
import {
  createBoard,
  DEFAULT_BOARD_SETTINGS,
  type BoardCreateRequest,
  type BoardSettings,
} from '../../utils/board';
import { invalidateBoardMenu } from '../../utils/boardMenu';
import { ArrowLeft, LayoutList, RefreshCw, Settings } from 'lucide-react';

const STATUS_OPTIONS_CREATE = [{ value: 'PENDING', label: '대기중' }];

const BOOL_OPTIONS = [
  { value: 'true', label: '사용' },
  { value: 'false', label: '미사용' },
];

const REACTION_OPTIONS = [
  { value: 'LIKE', label: 'LIKE' },
  { value: 'DISLIKE', label: 'DISLIKE' },
];

const MAX_REPLY_DEPTH_OPTIONS = [
  { value: '0', label: '0단계' },
  { value: '1', label: '1단계' },
  { value: '2', label: '2단계' },
  { value: '3', label: '3단계' },
];

const CODE_PATTERN = /^[a-zA-Z0-9_-]+$/;

interface FormErrors {
  code?: string;
  status?: string;
  name?: string;
  description?: string;
  max_reply_depth?: string;
  pin?: string;
  upload?: string;
}

const FormRow: React.FC<{
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
  error?: string;
}> = ({ label, required, hint, children, error }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-3 py-3 border-b border-gray-100 last:border-0">
    <div className="w-52 sm:w-56 flex-shrink-0">
      <p className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </p>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
    <div className="flex-1 min-w-0">
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  </div>
);

const OptionButtons: React.FC<{
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ options, value, onChange, disabled }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const selected = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => !disabled && onChange(opt.value)}
          disabled={disabled}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            selected ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          <span className={`w-2 h-2 rounded-full ${selected ? 'bg-white' : 'bg-gray-400'}`} />
          {opt.label}
        </button>
      );
    })}
  </div>
);

const MultiOptionButtons: React.FC<{
  options: { value: string; label: string }[];
  values: string[];
  onChange: (values: string[]) => void;
}> = ({ options, values, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const selected = values.includes(opt.value);
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => {
            if (selected) {
              onChange(values.filter((v) => v !== opt.value));
            } else {
              onChange([...values, opt.value]);
            }
          }}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            selected ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <span className={`w-2 h-2 rounded-full ${selected ? 'bg-white' : 'bg-gray-400'}`} />
          {opt.label}
        </button>
      );
    })}
  </div>
);

const boolToOption = (v: boolean) => (v ? 'true' : 'false');
const optionToBool = (v: string) => v === 'true';

const BoardCreate: React.FC = () => {
  const navigate = useNavigate();
  const { projectCode: projectCodeFromUrl } = useParams<{ projectCode: string }>();
  const boardMenu = useBoardMenuOptional();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('board-list');

  const [formData, setFormData] = useState({
    code: '',
    status: 'PENDING',
    name: '',
    description: '',
    settings: { ...DEFAULT_BOARD_SETTINGS },
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  const updateSettings = (patch: Partial<BoardSettings>) => {
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, ...patch },
    }));
  };

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    if (!projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
  }, [projectCodeFromUrl, navigate]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    const { settings } = formData;

    if (!formData.code.trim()) next.code = '게시판 코드는 필수입니다.';
    else if (formData.code.trim().length > 20) next.code = '코드는 최대 20자까지 입력할 수 있습니다.';
    else if (!CODE_PATTERN.test(formData.code.trim())) {
      next.code = '코드는 영문, 숫자, 언더스코어(_), 하이픈(-)만 사용할 수 있습니다.';
    }
    if (!formData.status) next.status = '상태를 선택하세요.';
    if (!formData.name.trim()) next.name = '이름은 필수입니다.';
    else if (formData.name.trim().length > 50) next.name = '이름은 최대 50자까지 입력할 수 있습니다.';
    if (formData.description.trim().length > 500) {
      next.description = '설명은 최대 500자까지 입력할 수 있습니다.';
    }

    if (settings.max_reply_depth < 0 || settings.max_reply_depth > 3) {
      next.max_reply_depth = '답글 단계는 0~3 사이로 입력하세요.';
    }
    if (!settings.reply && settings.max_reply_depth > 0) {
      next.max_reply_depth = '답글 미사용 시 답글 단계는 0이어야 합니다.';
    }
    if (settings.pin < 0) next.pin = '상단 고정수는 0 이상이어야 합니다.';
    if (settings.upload < 0) next.upload = '파일 업로드 수는 0 이상이어야 합니다.';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate() || !projectCodeFromUrl) return;

    try {
      setIsSubmitting(true);
      const payload: BoardCreateRequest = {
        code: formData.code.trim(),
        status: formData.status,
        name: formData.name.trim(),
        settings: {
          ...formData.settings,
          max_reply_depth: formData.settings.reply ? formData.settings.max_reply_depth : 0,
        },
      };
      if (formData.description.trim()) payload.description = formData.description.trim();

      await createBoard(projectCodeFromUrl, payload);
      if (boardMenu) {
        await boardMenu.invalidate();
      } else if (projectCodeFromUrl) {
        await invalidateBoardMenu(projectCodeFromUrl);
      }
      navigate(`/projects/${projectCodeFromUrl}/boards`, { replace: true });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '게시판 생성에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      code: '',
      status: 'PENDING',
      name: '',
      description: '',
      settings: { ...DEFAULT_BOARD_SETTINGS },
    });
    setErrors({});
    setSubmitError(null);
  };

  if (!projectDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-500">프로젝트 정보를 불러올 수 없습니다.</p>
          <button
            type="button"
            onClick={() => navigate('/projects')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            프로젝트 목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const { settings } = formData;

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="게시판 생성"
          subtitle={
            projectCodeFromUrl
              ? `${projectDetail.name} (${projectCodeFromUrl})`
              : '새 게시판을 등록합니다'
          }
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <button
                type="button"
                onClick={() => navigate(`/projects/${projectCodeFromUrl}/boards`)}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                게시판 목록으로
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {submitError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {submitError}
                </div>
              )}

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <LayoutList className="w-5 h-5" />
                    기본 정보
                  </h2>
                </div>
                <div className="px-4 lg:px-6 py-2 space-y-0">
                  <FormRow label="게시판 코드" required error={errors.code}>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => {
                        setFormData((p) => ({ ...p, code: e.target.value }));
                        setErrors((prev) => ({ ...prev, code: undefined }));
                      }}
                      placeholder="영문, 숫자, _, - (최대 20자)"
                      maxLength={20}
                      className={`block w-full min-w-[200px] px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.code ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </FormRow>
                  <FormRow label="상태" required error={errors.status}>
                    <div className="flex flex-wrap items-center gap-2">
                      <OptionButtons
                        options={STATUS_OPTIONS_CREATE}
                        value={formData.status}
                        onChange={(v) => setFormData((p) => ({ ...p, status: v }))}
                        disabled
                      />
                      <span className="text-sm text-gray-500">생성 시 상태는 대기중으로 설정됩니다.</span>
                    </div>
                  </FormRow>
                  <FormRow label="게시판 명칭" required error={errors.name}>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        setFormData((p) => ({ ...p, name: e.target.value }));
                        setErrors((prev) => ({ ...prev, name: undefined }));
                      }}
                      placeholder="게시판 이름 (최대 50자)"
                      maxLength={50}
                      className={`block w-full min-w-[200px] px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </FormRow>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-6">
                <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    게시판 설정
                  </h2>
                </div>
                <div className="px-4 lg:px-6 py-2 space-y-0">
                  <FormRow label="카테고리 사용 여부" hint="category">
                    <OptionButtons
                      options={BOOL_OPTIONS}
                      value={boolToOption(settings.category)}
                      onChange={(v) => updateSettings({ category: optionToBool(v) })}
                    />
                  </FormRow>
                  <FormRow label="게시물 반응" hint="reaction · LIKE, DISLIKE">
                    <MultiOptionButtons
                      options={REACTION_OPTIONS}
                      values={settings.reaction}
                      onChange={(reaction) => updateSettings({ reaction })}
                    />
                  </FormRow>
                  <FormRow label="조회수 표시" hint="view_count">
                    <OptionButtons
                      options={BOOL_OPTIONS}
                      value={boolToOption(settings.view_count)}
                      onChange={(v) => updateSettings({ view_count: optionToBool(v) })}
                    />
                  </FormRow>
                  <FormRow label="코멘트 여부" hint="comment">
                    <OptionButtons
                      options={BOOL_OPTIONS}
                      value={boolToOption(settings.comment)}
                      onChange={(v) => {
                        const comment = optionToBool(v);
                        updateSettings({
                          comment,
                          notify_comment: comment ? settings.notify_comment : false,
                        });
                      }}
                    />
                  </FormRow>
                  <FormRow label="코멘트 알림" hint="notify_comment">
                    <OptionButtons
                      options={BOOL_OPTIONS}
                      value={boolToOption(settings.notify_comment)}
                      onChange={(v) => updateSettings({ notify_comment: optionToBool(v) })}
                      disabled={!settings.comment}
                    />
                  </FormRow>
                  <FormRow label="답글 여부" hint="reply">
                    <OptionButtons
                      options={BOOL_OPTIONS}
                      value={boolToOption(settings.reply)}
                      onChange={(v) => {
                        const reply = optionToBool(v);
                        updateSettings({
                          reply,
                          max_reply_depth: reply ? settings.max_reply_depth : 0,
                          notify_reply: reply ? settings.notify_reply : false,
                        });
                      }}
                    />
                  </FormRow>
                  <FormRow label="답글 알림" hint="notify_reply">
                    <OptionButtons
                      options={BOOL_OPTIONS}
                      value={boolToOption(settings.notify_reply)}
                      onChange={(v) => updateSettings({ notify_reply: optionToBool(v) })}
                      disabled={!settings.reply}
                    />
                  </FormRow>
                  <FormRow
                    label="답글 단계"
                    hint="max_reply_depth · 최대 3단계 (원글 제외)"
                    error={errors.max_reply_depth}
                  >
                    <OptionButtons
                      options={MAX_REPLY_DEPTH_OPTIONS}
                      value={String(settings.max_reply_depth)}
                      onChange={(v) => updateSettings({ max_reply_depth: Number(v) })}
                      disabled={!settings.reply}
                    />
                  </FormRow>
                  <FormRow label="상단 고정수" hint="pin · 0: 없음" error={errors.pin}>
                    <input
                      type="number"
                      min={0}
                      value={settings.pin}
                      onChange={(e) => {
                        updateSettings({ pin: Number(e.target.value) || 0 });
                        setErrors((prev) => ({ ...prev, pin: undefined }));
                      }}
                      className={`block w-full max-w-[200px] px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.pin ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </FormRow>
                  <FormRow label="파일 업로드" hint="upload · 0: 없음" error={errors.upload}>
                    <input
                      type="number"
                      min={0}
                      value={settings.upload}
                      onChange={(e) => {
                        updateSettings({ upload: Number(e.target.value) || 0 });
                        setErrors((prev) => ({ ...prev, upload: undefined }));
                      }}
                      className={`block w-full max-w-[200px] px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.upload ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </FormRow>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-6">
                <div className="px-4 lg:px-6 py-2">
                  <FormRow label="설명" error={errors.description}>
                    <textarea
                      value={formData.description}
                      onChange={(e) => {
                        setFormData((p) => ({ ...p, description: e.target.value }));
                        setErrors((prev) => ({ ...prev, description: undefined }));
                      }}
                      placeholder="선택 입력 (최대 500자)"
                      rows={3}
                      maxLength={500}
                      className={`block w-full min-w-[200px] px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y ${
                        errors.description ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </FormRow>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6">
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center text-sm font-medium text-gray-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  초기화
                </button>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/projects/${projectCodeFromUrl}/boards`)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm font-medium"
                  >
                    {isSubmitting ? '생성 중...' : '게시판 생성'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default BoardCreate;
